import { defineStore } from 'pinia';
import { ref } from 'vue';
import type {
  CreditAccountView,
  CreditLedgerEntryView,
  CreditLedgerPage,
  CreditMonthView,
  CreditUsageSummary,
} from 'shared';
import { commerceApi } from '@/api/commerce.api';

export const useCreditStore = defineStore('credit', () => {
  const account = ref<CreditAccountView | null>(null);
  const ledger = ref<CreditLedgerEntryView[]>([]);
  const total = ref(0);
  const loading = ref(false);
  const summary = ref<CreditUsageSummary | null>(null),
    months = ref<CreditMonthView[]>([]),
    nextBefore = ref<string | null>(null);
  const period = ref(new Date(Date.now() + 8 * 3600_000).toISOString().slice(0, 7)),
    type = ref(''),
    channel = ref(''),
    day = ref('');
  const nextCursor = ref<string | null>(null),
    archiveStatus = ref<CreditLedgerPage['archiveStatus']>('online'),
    error = ref('');
  const active = ref(false),
    monthsLoading = ref(false);
  let reportWorkspaceId: string | undefined,
    request = 0,
    accountRequest = 0,
    monthRequest = 0;
  const query = () => ({
    workspaceId: reportWorkspaceId,
    month: ['30d', 'recent'].includes(period.value) ? undefined : period.value,
    period: ['30d', 'recent'].includes(period.value)
      ? (period.value as '30d' | 'recent')
      : undefined,
    type: type.value || undefined,
    channel: channel.value || undefined,
    day: day.value || undefined,
    limit: 20,
  });
  function applyPage(page: CreditLedgerPage, more: boolean) {
    ledger.value = more
      ? [
          ...ledger.value,
          ...page.items.filter((item) => !ledger.value.some((old) => old.id === item.id)),
        ]
      : page.items;
    total.value = page.total;
    nextCursor.value = page.nextCursor;
    archiveStatus.value = page.archiveStatus;
  }
  async function loadReport(force = false) {
    const version = ++request;
    loading.value = true;
    error.value = '';
    try {
      const [stats, page] = await Promise.all([
        commerceApi.creditSummary(period.value, reportWorkspaceId, force),
        commerceApi.creditHistory(query()),
      ]);
      if (version !== request) return;
      summary.value = stats;
      applyPage(page, false);
    } catch (e) {
      if (version === request) error.value = (e as Error).message || '积分统计加载失败';
    } finally {
      if (version === request) loading.value = false;
    }
  }
  async function loadMore() {
    if (!nextCursor.value || loading.value) return;
    const version = request;
    loading.value = true;
    try {
      const page = await commerceApi.creditHistory({ ...query(), cursor: nextCursor.value });
      if (version === request) applyPage(page, true);
    } catch (e) {
      if (version === request) error.value = (e as Error).message || '明细加载失败';
    } finally {
      if (version === request) loading.value = false;
    }
  }
  async function loadMonths(more = false) {
    if (more && (!nextBefore.value || monthsLoading.value)) return;
    const version = ++monthRequest;
    monthsLoading.value = true;
    try {
      const result = await commerceApi.creditMonths(
        more ? nextBefore.value! : undefined,
        reportWorkspaceId,
      );
      if (version === monthRequest) {
        months.value = more ? [...months.value, ...result.items] : result.items;
        nextBefore.value = result.nextBefore;
      }
    } catch (e) {
      if (version === monthRequest) error.value = (e as Error).message || '历史账单加载失败';
    } finally {
      if (version === monthRequest) monthsLoading.value = false;
    }
  }
  async function selectPeriod(value: string) {
    period.value = value;
    type.value = '';
    channel.value = '';
    day.value = '';
    ledger.value = [];
    summary.value = null;
    nextCursor.value = null;
    archiveStatus.value = 'online';
    total.value = 0;
    await loadReport();
  }
  async function restore() {
    try {
      await commerceApi.restoreCredits(period.value, reportWorkspaceId);
      await loadReport();
    } catch (e) {
      error.value = (e as Error).message || '归档恢复失败';
    }
  }
  function deactivate() {
    active.value = false;
    ++request;
    ++monthRequest;
    loading.value = false;
  }

  async function refreshAccount(workspaceId?: string, refreshReport = true): Promise<void> {
    const version = ++accountRequest;
    const result = await commerceApi.creditAccount(workspaceId);
    if (version === accountRequest) account.value = result;
    if (active.value && refreshReport) await loadReport(true);
  }
  async function loadLedger(page = 1, limit = 20, workspaceId?: string): Promise<void> {
    loading.value = true;
    try {
      const result = await commerceApi.creditLedger(page, limit, workspaceId);
      ledger.value = result.items;
      total.value = result.total;
    } finally {
      loading.value = false;
    }
  }
  async function refresh(page = 1, workspaceId?: string): Promise<void> {
    reportWorkspaceId = workspaceId;
    try {
      await Promise.all([refreshAccount(workspaceId, false), loadReport(true), loadMonths()]);
    } catch (e) {
      error.value = (e as Error).message || '积分数据加载失败';
    }
  }

  return {
    account,
    ledger,
    total,
    loading,
    summary,
    months,
    nextBefore,
    period,
    type,
    channel,
    day,
    nextCursor,
    archiveStatus,
    error,
    active,
    monthsLoading,
    refreshAccount,
    refresh,
    loadLedger,
    loadReport,
    loadMore,
    loadMonths,
    selectPeriod,
    restore,
    deactivate,
  };
});
